import { useCallback, useEffect, useRef } from 'react';
import { Envs, useEnvContext } from 'scbk-services';
import { usePopupStore } from '../stores/usePopupStore';

export interface UsePopupOptions {
  type?: 'popup' | 'dialog' | 'logout';
}

/**
 * 팝업 제어 hook
 * @param id 팝업 ID
 * @returns
 */
const usePopup = (id: string, options?: UsePopupOptions) => {
  const { getEnv } = useEnvContext();
  const triggerRef = useRef<HTMLElement | null>(null);
  const keyRef = useRef<string>();

  if (!keyRef.current) {
    keyRef.current = generatedPopupKey(id);
  }

  const key = keyRef.current;

  /**
   * 팝업 노출
   */
  const openPopup = usePopupStore((state) => state.open);

  /**
   * 팝업정보를 store에서 삭제한다.
   */
  const closePopup = usePopupStore((state) => state.close);
  const clearPopup = usePopupStore((state) => state.clear);
  const popupEntry = usePopupStore((state) => state.entry);

  /**
   * 팝업 열림/담힘여부 판단
   */
  const isOpen = usePopupStore((state) => state.entry[key]?.visible ?? false);

  /**
   * 팝업 open
   */
  const open = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      if (event) {
        triggerRef.current = (event?.currentTarget ?? document.activeElement) as HTMLElement;
      }
      openPopup(key, options);
    },
    [key],
  );

  /**
   * 팝업 close (토글형태)
   */
  const close = useCallback(() => {
    closePopup(key);

    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, [key]);

  /**
   *
   */
  const setOpen = useCallback((v: boolean) => {
    !v && close();
  }, []);

  /**
   * 팝업 open trigger element를 설정한다.
   */
  const setTrigger = useCallback(
    <T extends HTMLElement>(target?: T | null) => {
      triggerRef.current = target ?? (document.activeElement as HTMLElement);
    },
    [triggerRef.current],
  );

  //개발모드인경우에만 popup entry 로그 출력
  if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV || getEnv() === Envs.SIT) {
    useEffect(() => {
      console.log('# usePopup popupEntry', popupEntry);
    }, [popupEntry, key]);
  }

  /**
   * 팝업 클린업
   */
  useEffect(() => {
    return () => {
      //언마운트 시 처리 팝업을 닫는다.
      console.log('# usePopup 언마운트 popup clear key=', key);
      clearPopup(key);
      //trigger ref 초기화
      triggerRef.current = null;
    };
  }, [key]);

  return {
    isOpen,
    open,
    close,
    setOpen,
    setTrigger,
  };
};

const generatedUUID = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

/**
 * 팝업정보 store uid 생성
 * @param prefix
 * @returns
 */
const generatedPopupKey = (prefix: string = 'popup') => {
  const uuid = generatedUUID();
  if (prefix === '') {
    return `${uuid}`;
  }
  return `${prefix}-${uuid}`;
};

export { generatedPopupKey, usePopup };
